import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import useSingleLead from "../../../data/useSingleLead";
import { ILead, QuoteType } from "../../../models/Lead";
import Card from "../../shared/Card/Card";
import LoadingWheel from "../../shared/LoadingWheel";
import { BodyCondition, EngineCondition } from "../../../models/Vehicle";
import { GetQuoteType } from "./QuoteCalculationCard";
import { formatCollectionOption, hasCollectionOption } from "../../../utils/leadPaymentOption";


const SingleDetail = ({ title, value, textColour = "text-gray-900", valueClassName = "" }: { title: string; value: string | null | undefined; textColour?: string; valueClassName?: string; }) => {
  return (
    <div className="py-2 grid grid-cols-2 gap-4">
      <dt className="text-sm font-medium text-gray-500">{title}</dt>
      <dd className={`${textColour} text-sm ${valueClassName}`}>{value}</dd>
    </div>
  );
};

/**
 * Renders a condition answer, preferring the lead's own snapshot of what the customer said.
 *
 * A registration shares one vehicle row across every lead, and each new widget lead overwrites its
 * condition fields — so `lead.vehicle.*` is the car's latest known state, which on a second quote
 * is a *different* customer's answers. Leads created before the widget started snapshotting have
 * nothing else to show, so they fall back to the vehicle and are labelled, rather than presenting
 * someone else's answer as this customer's.
 */
function quotedCondition<T>(
  quoted: T | null | undefined,
  format: (value: T) => string,
  vehicleFallback: T | null | undefined
): string {
  if (quoted !== null && quoted !== undefined) return format(quoted);
  if (vehicleFallback !== null && vehicleFallback !== undefined) {
    return `${format(vehicleFallback)} (latest known)`;
  }
  return "N/A";
}

export default function WidgetQuoteDetails({ leadId }: { leadId: string }) {
  const { t } = useTranslation("leads");
  const { lead: singleLead } = useSingleLead(leadId, "WidgetQuoteDetails");
  const [lead, setLead] = useState<ILead | undefined>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!singleLead.isLoading && singleLead.data) {
      setLead(singleLead.data.data);
      setIsLoading(false);
    }
  }, [singleLead.isLoading, singleLead.data]);

  if (isLoading || lead == undefined) return <Card bodyClassName="p-4" title={t("Quote Details")}><LoadingWheel /></Card>;

  return (
    <Card bodyClassName="p-4 divide-y divide-gray-200" title={t("Quote Details")} collapsible>
      <SingleDetail title={t("Quote Type")} value={t(GetQuoteType(lead.quoteType!) ?? "")} />
      {hasCollectionOption(lead) ? (
        <SingleDetail title={t("Collection Option")} value={formatCollectionOption(lead)} />
      ) : (
        <>
          <SingleDetail title={t("Road Side Payment")} value={lead.roadSidePayment ? t("Yes") : t("No")} />
          <SingleDetail title={t("Fast Payment")} value={lead.fastPayment ? t("Yes") : t("No")} />
        </>
      )}
      <SingleDetail title={t("Access To Vehicle")} value={lead.accessToVehicle ? t("Yes") : t("No")} />
      <SingleDetail title={t("V5C")} value={lead.v5c ? t("Yes") : t("No")} />
      <SingleDetail title={t("Collection")} value={lead.isCollection ? t("Yes") : t("No")} />
      {lead.preferredCollectionDate && (
        <SingleDetail title={t("Preferred Collection Date")} value={new Date(lead.preferredCollectionDate).toLocaleDateString("en-GB")} />
      )}
      <SingleDetail
        title={t("Engine Condition")}
        value={quotedCondition(
          lead.quotedEngineCondition,
          (v) => t(EngineCondition[v]),
          lead.vehicle?.engineCondition
        )}
      />
      <SingleDetail
        title={t("Body Condition")}
        value={quotedCondition(
          lead.quotedBodyCondition,
          (v) => t(BodyCondition[v]),
          lead.vehicle?.bodyCondition
        )}
      />
      <SingleDetail
        title={t("Alloy Wheels")}
        value={quotedCondition(
          lead.quotedAlloyWheels,
          (v) => (v ? t("Yes") : t("No")),
          lead.vehicle?.alloyWheels
        )}
      />
      <SingleDetail
        title={t("Mileage")}
        value={quotedCondition(
          lead.quotedMileage,
          (v) => v.toLocaleString("en-GB"),
          lead.vehicle?.odometerInMiles
        )}
      />
      {/* Only paid clicks carry one, so the row stays hidden rather than showing an empty value. */}
      {lead.gclid && (
        <SingleDetail title={t("Google Click ID")} value={lead.gclid} valueClassName="font-mono break-all select-all" />
      )}
    </Card>
  );
};